import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FamilyIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterHostFamilyCosts } from '../../../../api/centers/center-host-family-costs/get-center-host-family-costs'
import { updateCenterHostFamilyCosts } from '../../../../api/centers/center-host-family-costs/update-center-host-family-costs'
import { getCenter } from '../../../../api/centers/get-center'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterHostFamilyCost,
  CenterHostFamilyCostInput
} from '../../../../lib/types/models/center/center-host-family-cost'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { DataProps } from '../../../../lib/types/utils'
import EditableTable from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton
} from './center.ui'
import {
  CenterCostsTableProps,
  getCenterHostFamilyCostsColumns,
  mapCenterHostFamilyCostToInput
} from './utils'

export function CenterHostFamilyCostsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [hostFamilyCosts, setHostFamilyCosts] = useState<CenterHostFamilyCostInput[]>()

  const {
    id: centerId,
    data,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { data: centerHostFamilyCosts, refetch } = useQuery({
    queryKey: ['center-host-family-costs', centerId],
    queryFn: () => getCenterHostFamilyCosts(centerId)
  })

  useObserver({
    key: 'center-host-family-costs-table',
    update: refetch
  })

  const { centerCostsView, lock, unlock } = useCenterCosts({
    centerId,
    costType,
    table: 'host-family'
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: CenterHostFamilyCostInput[]) => updateCenterHostFamilyCosts(centerId, data),
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
      setHostFamilyCosts(undefined)
    }
  })

  const productTypes = data ? CenterProperties.getCenterProductTypes(data) : []

  if (!user || !centerHostFamilyCosts || !data) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={FamilyIcon}
        title={t('center.costs.host.family.title')}
        onCancel={() => setHostFamilyCosts(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: data.isHostFamiliesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (hostFamilyCosts) {
            await mutateAsync(hostFamilyCosts)
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterHostFamilyCost>
        isEditable={editable}
        data={centerHostFamilyCosts.data}
        columns={getCenterHostFamilyCostsColumns(costType, currency, t, productTypes)}
        onDataChange={(data) => {
          const mappedData = data.map((item) => mapCenterHostFamilyCostToInput(item, costType))
          if (mode === 'free-edit') mutateAsync(mappedData)
          else setHostFamilyCosts(mappedData)
        }}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.host.family.table.footer.total.host.family')
            }
            total={centerCostsView.data?.totalHostFamilyCost}
            initialAmount={centerCostsView.data?.totalHostFamilyCost}
            finalAmount={centerCostsView.data?.finalTotalHostFamilyCost}
            balance={centerCostsView.data?.hostFamilyBalance}
          />
        }
      />
    </div>
  )
}
